import { useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "@uidotdev/usehooks";
import { differenceInCalendarDays, format, isValid } from "date-fns";
import {
  ArrowDownTrayIcon,
  DocumentChartBarIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon as PaperAirplaneOutlineIcon,
  ReceiptRefundIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";
import { toast } from "@components/toast/Toast";
import { Badge } from "@components";
import Tooltip from "@components/Tooltip";
import { formatAmount, cn } from "@utils";
import {
  useCustomerDocumentInvoices,
  downloadDocument,
  downloadProofOfDeliveries,
} from "@api/documentsController";
import {
  GhostIconButton,
  SelectionBar,
  SINGLE_LINE_TOOLTIP,
  Spinner,
  useDocumentActions,
} from "./documentActions";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "invoices", label: "Invoices" },
  { key: "pods", label: "Proof of delivery" },
  { key: "statements", label: "Statements" },
  { key: "creditNotes", label: "Credit notes" },
];

const KIND_META = {
  invoice: { icon: DocumentTextIcon },
  pod: { icon: TruckIcon },
  creditNote: { icon: ReceiptRefundIcon },
  statement: { icon: DocumentChartBarIcon },
};

function fmtDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return isValid(d) ? format(d, "d MMM yy") : null;
}

function invoiceSubtitle({ issuedDate, dueDate }, status) {
  const parts = [];
  const issued = fmtDate(issuedDate);
  if (issued) parts.push(`Issued ${issued}`);
  if (dueDate && status !== "paid") {
    const due = new Date(dueDate);
    if (isValid(due)) {
      const overdueBy = differenceInCalendarDays(new Date(), due);
      parts.push(overdueBy > 0 ? `${overdueBy} days overdue` : `due ${format(due, "d MMM")}`);
    }
  }
  return parts.join(" · ");
}

function paymentStatus({ isCreditNote, amount, outstanding }) {
  if (isCreditNote || amount == null || outstanding == null) return null;
  if (Math.abs(outstanding) < 0.005) return "paid";
  return Math.abs(outstanding) < Math.abs(amount) ? "partial" : null;
}

const STATUS_BADGES = {
  paid: { label: "Paid", color: "green" },
  partial: { label: "Partially paid", color: "yellow" },
};

function DocumentRow({ row, selected, failed, busy, sendBusy, onToggle, onDownload, onSend }) {
  const Icon = (KIND_META[row.kind] ?? KIND_META.invoice).icon;
  const isPod = row.kind === "pod";
  return (
    <div
      className={cn(
        "group flex items-center gap-3 px-4 py-3 transition-colors",
        "hover:bg-zinc-50",
        selected && "bg-zinc-50/60",
      )}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        aria-label={`Select ${row.title}`}
        className="size-4 shrink-0 rounded border-zinc-300 accent-primary-500"
      />
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-zinc-200/50 text-zinc-500">
        <Icon aria-hidden="true" className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold text-zinc-900">{row.title}</span>
          {STATUS_BADGES[row.status] && (
            <Tooltip
              content={row.status === "partial" ? `${formatAmount(Math.abs(row.remaining))} remaining` : null}
              wrapperClassName="flex shrink-0"
              className={SINGLE_LINE_TOOLTIP}
            >
              <Badge color={STATUS_BADGES[row.status].color}>{STATUS_BADGES[row.status].label}</Badge>
            </Tooltip>
          )}
        </div>
        {row.subtitle && <div className="mt-0.5 truncate text-xs text-zinc-500">{row.subtitle}</div>}
        {failed && <div className="mt-0.5 text-xs font-medium text-red-600">Unavailable</div>}
      </div>
      {/* Amount and actions share one grid cell so hover swaps them without shifting the row; a running
          action keeps the buttons up so its spinner stays visible. The faded amount paints above the buttons
          (opacity < 1 creates a stacking context), hence pointer-events-none on it. */}
      <div className="grid shrink-0 items-center justify-items-end">
        {row.amount != null && (
          <span
            className={cn(
              "pointer-events-none col-start-1 row-start-1 text-sm font-medium tabular-nums text-zinc-900 transition-opacity",
              "group-hover:opacity-0 group-has-[:focus-visible]:opacity-0",
              (busy || sendBusy) && "opacity-0",
            )}
          >
            {formatAmount(row.kind === "creditNote" ? -Math.abs(row.amount) : row.amount)}
          </span>
        )}
        <div
          className={cn(
            "col-start-1 row-start-1 flex items-center gap-1 transition-opacity",
            "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100",
            "group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:opacity-100",
            (busy || sendBusy) && "opacity-100",
          )}
        >
          <GhostIconButton
            icon={ArrowDownTrayIcon}
            busy={busy}
            label={`Download ${row.title}`}
            tooltip={isPod ? (busy ? "Checking for POD…" : "Check availability & download") : busy ? "Downloading…" : "Download"}
            onClick={onDownload}
          />
          <GhostIconButton
            icon={PaperAirplaneOutlineIcon}
            busy={sendBusy}
            label={`Send ${row.title}`}
            tooltip={isPod ? (sendBusy ? "Checking for POD…" : "Check availability, preview & send") : sendBusy ? "Preparing…" : "Preview & send"}
            onClick={onSend}
          />
        </div>
      </div>
    </div>
  );
}

function Chip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
        active
          ? "border-zinc-300 bg-zinc-100 text-zinc-900"
          : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50",
      )}
    >
      {label}
    </button>
  );
}

// The server kind each filter maps to. Invoices and PODs share the Invoice kind (a POD is one-per-invoice);
// credit notes are their own kind; "All" has no kind; Statements isn't a server list at all.
const KIND_BY_FILTER = { all: undefined, invoices: "Invoice", creditNotes: "CreditNote", pods: "Invoice" };

export default function DocumentsTab({ customer, detail, scrollContainerRef }) {
  const actions = useDocumentActions({ customer, detail });
  const { accountCode, selected, toggleSelect, busy, failedIds, downloadRow, openSendFor } = actions;
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Debounced so each keystroke doesn't fire a request; the server does the searching.
  const debouncedSearch = useDebounce(search.trim(), 300);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCustomerDocumentInvoices(accountCode, {
    kind: KIND_BY_FILTER[filter],
    search: debouncedSearch,
    // Statements are a single synthetic row — no server list to fetch for that filter.
    enabled: filter !== "statements",
  });

  const items = useMemo(() => data?.pages.flatMap((page) => page?.items ?? []) ?? [], [data]);

  const invoiceRow = (inv) => {
    const status = paymentStatus(inv);
    return {
      id: `inv-${inv.number}`,
      number: inv.number,
      kind: inv.isCreditNote ? "creditNote" : "invoice",
      title: `${inv.isCreditNote ? "Credit note" : "Invoice"} ${inv.number}`,
      status,
      remaining: inv.outstanding,
      subtitle: invoiceSubtitle(inv, status),
      amount: inv.amount ?? null,
      spec: { documentType: "Invoice", invoiceNumbers: [inv.number] },
      download: () =>
        downloadDocument(accountCode, {
          documentType: "Invoice",
          invoiceNumbers: [inv.number],
          fileName: `${inv.number}.pdf`,
        }),
    };
  };

  // Proof-of-delivery rows are assumed one per invoice (no listing lookup); the materialize on
  // download/send is what actually confirms one exists, and we fail gracefully if it doesn't.
  const podRow = (inv) => ({
    id: `pod-${inv.number}`,
    number: inv.number,
    kind: "pod",
    title: `Proof of delivery · ${inv.number}`,
    subtitle: null,
    amount: null,
    spec: { documentType: "ProofOfDelivery", invoiceNumbers: [inv.number] },
    download: async () => {
      const count = await downloadProofOfDeliveries(accountCode, inv.number);
      if (count === 0) {
        toast({ description: `No proof of delivery found for invoice ${inv.number}.` });
      } else if (count > 1) {
        toast({ description: `Downloaded ${count} proofs of delivery for ${inv.number} (zipped).` });
      }
    },
  });

  const statementRow = {
    id: "statement",
    kind: "statement",
    title: "Account statement",
    subtitle: "Current balance · PDF",
    // The statement's figure is the account's outstanding balance (what the PDF totals to).
    amount: detail?.balance ?? null,
    spec: { documentType: "Statement" },
    download: () =>
      downloadDocument(accountCode, { documentType: "Statement", fileName: "statement.pdf" }),
  };

  // Rows for the active filter. Documents stream from the paged server query (searched + kind-filtered
  // server-side). PODs are one-per-invoice: their own list under "Proof of delivery", and — under "All" —
  // emitted right after each invoice (credit notes have no POD) so they interleave without breaking paging.
  // The statement is a single synthetic row: the whole list under "Statements", pinned atop "All".
  const rows = useMemo(() => {
    if (filter === "statements") return [statementRow];
    if (filter === "pods") return items.map(podRow);
    if (filter === "invoices" || filter === "creditNotes") return items.map(invoiceRow);
    const docRows = items.flatMap((inv) =>
      inv.isCreditNote ? [invoiceRow(inv)] : [invoiceRow(inv), podRow(inv)]);
    return [statementRow, ...docRows];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, items, detail?.balance]);

  // Infinite scroll: a tail sentinel pulls the next page as it nears the panel's scroll viewport. Mirrors
  // the activity feed — a fixed-height band so the loader toggling never shifts layout.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { root: scrollContainerRef?.current ?? null, rootMargin: "240px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, scrollContainerRef]);

  return (
    <div className="flex min-h-full flex-col gap-3">
      {/* Search */}
      <div className="relative">
        <MagnifyingGlassIcon
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search documents"
          className="w-full rounded-xl border border-zinc-200 bg-white py-2.5 pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-primary-100"
        />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Chip
            key={f.key}
            label={f.label}
            active={filter === f.key}
            onClick={() => setFilter(f.key)}
          />
        ))}
      </div>

      {/* Document list */}
      {isError ? (
        <div className="px-4 py-6 text-sm text-zinc-400">Couldn’t load documents.</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white">
          <div className="divide-y divide-zinc-100">
            {rows.map((row) => (
              <DocumentRow
                key={row.id}
                row={row}
                selected={selected.has(row.id)}
                failed={failedIds.has(row.id)}
                busy={busy.has(row.id)}
                sendBusy={busy.has(`send-${row.id}`)}
                onToggle={() => toggleSelect(row)}
                onDownload={() => downloadRow(row)}
                onSend={() => openSendFor([row], `send-${row.id}`)}
              />
            ))}

            {isLoading && (
              <div className="flex items-center gap-2 px-4 py-6 text-sm text-zinc-400">
                <Spinner className="size-4" />
                Loading documents…
              </div>
            )}

            {!isLoading && rows.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-zinc-400">
                {debouncedSearch ? `No documents match “${debouncedSearch}”.` : "No documents to show."}
              </div>
            )}

            {/* Tail sentinel + loader — a fixed-height band so the loader toggling never shifts the list. */}
            {hasNextPage && (
              <div
                ref={sentinelRef}
                aria-hidden="true"
                className="flex h-11 items-center justify-center"
              >
                {isFetchingNextPage && <Spinner className="size-4 text-zinc-400" />}
              </div>
            )}
          </div>
        </div>
      )}

      <SelectionBar actions={actions} />

      {actions.sendModal}
    </div>
  );
}
